import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  StopIcon,
  CheckCircleIcon,
  MapPinIcon,
  TruckIcon,
  UsersIcon,
  ArrowPathIcon,
  CogIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FunnelIcon,
  SignalIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import {
  getTrips,
  getTripById,
  getTripStats,
  startTrip,
  completeTrip,
  updateTrip,
  deleteTrip,
  type Trip,
  type TripStats,
  type UpdateTripRequest
} from '@/api/tripsApi'
import EnhancedTripForm from '@/components/transport/EnhancedTripForm'
import TripSessionsManager from '@/components/transport/TripSessionsManager'
import TransportSettingsComponent from '@/components/transport/TransportSettings'
import { getRoutes, type TransportRoute } from '@/api/routesApi'
import { getVehicles, type Vehicle } from '@/api/fleetApi'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

type TabType = 'overview' | 'trips' | 'sessions' | 'settings'
type ViewMode = 'grid' | 'table'

const TABS = [
  { id: 'overview' as const, name: 'Overview', icon: CalendarDaysIcon },
  { id: 'trips' as const, name: 'Trips', icon: TruckIcon },
  { id: 'sessions' as const, name: 'Sessions', icon: PlayIcon },
  { id: 'settings' as const, name: 'Settings', icon: CogIcon }
]

const STATUS_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  active: { color: 'text-green-700', bgColor: 'bg-green-100', label: 'Active' },
  inactive: { color: 'text-gray-700', bgColor: 'bg-gray-100', label: 'Inactive' },
  suspended: { color: 'text-red-700', bgColor: 'bg-red-100', label: 'Suspended' },
  scheduled: { color: 'text-blue-700', bgColor: 'bg-blue-100', label: 'Scheduled' },
  in_progress: { color: 'text-green-700', bgColor: 'bg-green-100', label: 'Live' },
  completed: { color: 'text-gray-700', bgColor: 'bg-gray-100', label: 'Completed' },
  cancelled: { color: 'text-red-700', bgColor: 'bg-red-100', label: 'Cancelled' },
  delayed: { color: 'text-yellow-700', bgColor: 'bg-yellow-100', label: 'Delayed' }
}

const TRIP_TYPE_CONFIG: Record<string, { color: string; bgColor: string; label: string; icon: any }> = {
  pickup: { color: 'text-green-700', bgColor: 'bg-green-50', label: 'Pickup', icon: PlayIcon },
  dropoff: { color: 'text-red-700', bgColor: 'bg-red-50', label: 'Drop-off', icon: StopIcon },
  field_trip: { color: 'text-blue-700', bgColor: 'bg-blue-50', label: 'Field Trip', icon: MapPinIcon },
  emergency: { color: 'text-orange-700', bgColor: 'bg-orange-50', label: 'Emergency', icon: ExclamationTriangleIcon },
  custom: { color: 'text-purple-700', bgColor: 'bg-purple-50', label: 'Custom', icon: CalendarDaysIcon }
}

export default function TripsPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')

  // Data state
  const [trips, setTrips] = useState<Trip[]>([])
  const [stats, setStats] = useState<TripStats | null>(null)
  const [routes, setRoutes] = useState<TransportRoute[]>([])
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null)

  // UI state
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [routeFilter, setRouteFilter] = useState('all')
  const [institutionFilter, setInstitutionFilter] = useState('all')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalTrips, setTotalTrips] = useState(0)
  const pageSize = 12

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  // Fetch trips
  const fetchTrips = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setIsRefreshing(true)

      const params: Record<string, any> = {
        page: currentPage,
        limit: pageSize,
        search: searchQuery.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        tripType: typeFilter === 'all' ? undefined : typeFilter,
        routeId: routeFilter === 'all' ? undefined : routeFilter,
        institutionId: isSuperAdmin && institutionFilter !== 'all' ? institutionFilter : undefined
      }

      const response = await getTrips(params)
      setTrips(response.data?.trips || [])
      setTotalPages(response.data?.pagination?.totalPages || 1)
      setTotalTrips(response.data?.pagination?.totalTrips || 0)
      setLastRefresh(new Date())
    } catch (error) {
      console.error('Failed to fetch trips:', error)
    } finally {
      if (showRefreshIndicator) setIsRefreshing(false)
    }
  }, [currentPage, searchQuery, statusFilter, typeFilter, routeFilter, institutionFilter, isSuperAdmin, pageSize])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const instId = isSuperAdmin && institutionFilter !== 'all' ? institutionFilter : undefined
      const response = await getTripStats(instId)
      setStats(response.data || null)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }, [institutionFilter, isSuperAdmin])

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const instId = isSuperAdmin && institutionFilter !== 'all' ? institutionFilter : undefined

      const [routesRes, vehiclesRes] = await Promise.all([
        getRoutes({ institutionId: instId }),
        getVehicles({ institutionId: instId, status: 'active' })
      ])

      setRoutes(routesRes.data?.routes || [])
      setVehicles(vehiclesRes.data?.vehicles || [])

      if (isSuperAdmin) {
        const instRes = await institutionsApi.getInstitutions()
        setInstitutions(instRes.institutions || [])
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error)
    }
  }, [institutionFilter, isSuperAdmin])

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchTrips(), fetchStats(), fetchFilterOptions()])
      setLoading(false)
    }
    loadData()
  }, [])

  // Reload on filter changes
  useEffect(() => {
    fetchTrips()
    fetchStats()
  }, [currentPage, searchQuery, statusFilter, typeFilter, routeFilter, institutionFilter])

  // Auto-refresh for live data
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'overview' || activeTab === 'trips') {
        fetchTrips()
        fetchStats()
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [activeTab, fetchTrips, fetchStats])

  // Manual refresh
  const handleRefresh = () => {
    fetchTrips(true)
    fetchStats()
  }

  // Trip actions
  const handleStartTrip = async (trip: Trip) => {
    const tripId = trip.id || (trip as any)._id
    if (!tripId) {
      toast.error('Trip ID is missing')
      return
    }
    try {
      await startTrip(tripId)
      toast.success('Trip started!')
      fetchTrips()
      fetchStats()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to start trip')
    }
  }

  const handleCompleteTrip = async (trip: Trip) => {
    const tripId = trip.id || (trip as any)._id
    if (!tripId) {
      toast.error('Trip ID is missing')
      return
    }
    try {
      await completeTrip(tripId)
      toast.success('Trip completed!')
      fetchTrips()
      fetchStats()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to complete trip')
    }
  }

  const handleViewTrip = async (trip: Trip) => {
    const tripId = trip.id || (trip as any)._id
    try {
      const response = await getTripById(tripId)
      setSelectedTrip(response.data?.trip || trip)
      setShowViewModal(true)
    } catch (error) {
      setSelectedTrip(trip)
      setShowViewModal(true)
    }
  }

  const handleEditTrip = (trip: Trip) => {
    setSelectedTrip(trip)
    setShowEditModal(true)
  }

  const handleDeleteTrip = (trip: Trip) => {
    setSelectedTrip(trip)
    setShowDeleteModal(true)
  }

  const confirmDeleteTrip = async () => {
    if (!selectedTrip) return
    const tripId = selectedTrip.id || (selectedTrip as any)._id
    try {
      await deleteTrip(tripId)
      toast.success('Trip deleted successfully')
      setShowDeleteModal(false)
      setSelectedTrip(null)
      fetchTrips()
      fetchStats()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete trip')
    }
  }

  const handleUpdateTrip = async (data: UpdateTripRequest) => {
    if (!selectedTrip) return
    const tripId = selectedTrip.id || (selectedTrip as any)._id
    try {
      await updateTrip(tripId, data)
      toast.success('Trip updated successfully')
      setShowEditModal(false)
      setSelectedTrip(null)
      fetchTrips()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update trip')
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setTypeFilter('all')
    setRouteFilter('all')
    setInstitutionFilter('all')
    setCurrentPage(1)
  }

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || routeFilter !== 'all' || (isSuperAdmin && institutionFilter !== 'all')

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || { color: 'text-gray-700', bgColor: 'bg-gray-100', label: status }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${config.bgColor} ${config.color}`}>
        {status === 'in_progress' && <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse" />}
        {config.label}
      </span>
    )
  }

  const getTripTypeBadge = (type: string) => {
    const config = TRIP_TYPE_CONFIG[type] || TRIP_TYPE_CONFIG.custom
    const Icon = config.icon
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${config.bgColor} ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    )
  }

  const canManageTrip = (trip: Trip) => {
    return trip.status === 'active' && (user?.role !== 'DRIVER' || trip.primaryDriverId === user.id)
  }

  // Stats Card Component
  const StatsCard = ({ title, value, icon: Icon, color, subValue }: {
    title: string
    value: number
    icon: any
    color: string
    subValue?: string
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border shadow-sm p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color.replace('text-', 'bg-').replace('-600', '-100').replace('-700', '-100')}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </motion.div>
  )

  // Trip Card Component
  const TripCard = ({ trip }: { trip: Trip }) => {
    const tripId = trip.id || (trip as any)._id
    const typeConfig = TRIP_TYPE_CONFIG[trip.tripType] || TRIP_TYPE_CONFIG.custom
    const TypeIcon = typeConfig.icon

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-all overflow-hidden group"
      >
        <div className={`h-1 ${typeConfig.bgColor.replace('bg-', 'bg-').replace('-50', '-400')}`} />
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig.bgColor}`}>
                <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 line-clamp-1">
                  {trip.tripName || `${trip.tripType} Trip`}
                </h3>
                <p className="text-xs text-gray-500">{getTripTypeBadge(trip.tripType)}</p>
              </div>
            </div>
            {getStatusBadge(trip.status)}
          </div>

          {/* Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
              <span className="line-clamp-1">{trip.route?.routeName || trip.route?.routeNumber || 'No route'}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <TruckIcon className="w-4 h-4 mr-2 text-gray-400" />
              <span>{trip.vehicle?.vehicleNumber || 'No vehicle'}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
              <span>{trip.startTime || '--:--'} - {trip.endTime || '--:--'}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <UsersIcon className="w-4 h-4 mr-2 text-gray-400" />
              <span>{trip.studentCount || trip.currentEnrollment || 0} / {trip.capacity || 0} students</span>
            </div>
          </div>

          {/* Operating Days */}
          <div className="flex flex-wrap gap-1 mb-4">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
              const dayFull = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][idx]
              const isActive = trip.operatingDays?.includes(dayFull as any)
              return (
                <span
                  key={day}
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-400'
                  }`}
                >
                  {day}
                </span>
              )
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handleViewTrip(trip)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="View Details"
              >
                <EyeIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleEditTrip(trip)}
                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title="Edit Trip"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteTrip(trip)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Trip"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center space-x-2">
              {canManageTrip(trip) && trip.status === 'active' && (
                <Button
                  onClick={() => handleStartTrip(trip)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <PlayIcon className="h-3 w-3 mr-1" />
                  Start
                </Button>
              )}
              {trip.status === 'in_progress' && (
                <Button
                  onClick={() => handleCompleteTrip(trip)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  <StopIcon className="h-3 w-3 mr-1" />
                  End
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trip Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage transport trips, sessions, and schedules
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Live Indicator */}
          <div className="hidden sm:flex items-center text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full">
            <SignalIcon className="w-3 h-3 mr-1.5 text-green-500" />
            <span>Auto-refresh: 30s</span>
          </div>

          {/* Last Refresh */}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center text-xs text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-full transition-colors"
          >
            <ArrowPathIcon className={`w-3 h-3 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : `Last: ${lastRefresh.toLocaleTimeString()}`}
          </button>

          <Button onClick={() => setShowCreateModal(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Trip
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Trips"
          value={stats?.totalTrips || 0}
          icon={TruckIcon}
          color="text-blue-600"
          subValue="All configured trips"
        />
        <StatsCard
          title="Active Trips"
          value={stats?.scheduled || 0}
          icon={CheckCircleIcon}
          color="text-green-600"
          subValue="Ready to run"
        />
        <StatsCard
          title="In Progress"
          value={stats?.inProgress || 0}
          icon={PlayIcon}
          color="text-orange-600"
          subValue="Currently running"
        />
        <StatsCard
          title="Completed Today"
          value={stats?.completedTrips || 0}
          icon={ClockIcon}
          color="text-purple-600"
          subValue="Finished today"
        />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="border-b">
          <nav className="flex space-x-1 px-4 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span>{tab.name}</span>
                {tab.id === 'trips' && totalTrips > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {totalTrips}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Today's Sessions</h3>
                <TripSessionsManager
                  institutionId={user?.institutionId}
                  viewMode="today"
                  driverId={user?.role === 'DRIVER' ? user.id : undefined}
                />
              </div>
            </div>
          )}

          {/* Trips Tab */}
          {activeTab === 'trips' && (
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                {/* Search */}
                <div className="relative w-full sm:w-80">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search trips..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* View Controls */}
                <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                  {/* Filter Toggle */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center px-3 py-2 text-sm rounded-lg border transition-colors ${
                      hasActiveFilters
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <FunnelIcon className="w-4 h-4 mr-2" />
                    Filters
                    {hasActiveFilters && (
                      <span className="ml-1.5 w-2 h-2 bg-blue-500 rounded-full" />
                    )}
                    {showFilters ? (
                      <ChevronUpIcon className="w-4 h-4 ml-2" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 ml-2" />
                    )}
                  </button>

                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-white shadow text-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Squares2X2Icon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'table'
                          ? 'bg-white shadow text-blue-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <ListBulletIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Filter Panel */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>

                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value="all">All Types</option>
                        <option value="pickup">Pickup</option>
                        <option value="dropoff">Drop-off</option>
                        <option value="field_trip">Field Trip</option>
                        <option value="emergency">Emergency</option>
                      </select>

                      <select
                        value={routeFilter}
                        onChange={(e) => setRouteFilter(e.target.value)}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                      >
                        <option value="all">All Routes</option>
                        {routes.map(route => (
                          <option key={route.id} value={route.id}>
                            {route.routeNumber} - {route.routeName}
                          </option>
                        ))}
                      </select>

                      {isSuperAdmin && (
                        <select
                          value={institutionFilter}
                          onChange={(e) => setInstitutionFilter(e.target.value)}
                          className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                        >
                          <option value="all">All Institutions</option>
                          {institutions.map(inst => (
                            <option key={inst.id} value={inst.id}>{inst.name}</option>
                          ))}
                        </select>
                      )}

                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Content */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
                  <p className="text-gray-500">Loading trips...</p>
                </div>
              ) : trips.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TruckIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No trips found</h3>
                  <p className="text-gray-500 mb-4">
                    {hasActiveFilters
                      ? 'Try adjusting your filters'
                      : 'Create your first trip to get started'}
                  </p>
                  {hasActiveFilters ? (
                    <Button variant="secondary" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  ) : (
                    <Button onClick={() => setShowCreateModal(true)}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create Trip
                    </Button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                /* Grid View */
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {trips.map((trip) => (
                    <TripCard key={trip.id || (trip as any)._id} trip={trip} />
                  ))}
                </div>
              ) : (
                /* Table View */
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trip</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {trips.map((trip) => (
                        <tr key={trip.id || (trip as any)._id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${TRIP_TYPE_CONFIG[trip.tripType]?.bgColor || 'bg-gray-50'}`}>
                                {(() => {
                                  const Icon = TRIP_TYPE_CONFIG[trip.tripType]?.icon || CalendarDaysIcon
                                  return <Icon className={`w-4 h-4 ${TRIP_TYPE_CONFIG[trip.tripType]?.color || 'text-gray-600'}`} />
                                })()}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {trip.tripName || `${trip.tripType} Trip`}
                                </p>
                                <p className="text-xs text-gray-500 capitalize">{trip.tripType.replace('_', ' ')}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm text-gray-900">{trip.route?.routeNumber || '-'}</p>
                            <p className="text-xs text-gray-500">{trip.route?.routeName || '-'}</p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm text-gray-900">{trip.vehicle?.vehicleNumber || 'Not assigned'}</p>
                            <p className="text-xs text-gray-500">
                              {trip.primaryDriver?.firstName} {trip.primaryDriver?.lastName}
                            </p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <p className="text-sm text-gray-900">
                              {trip.startTime || '--:--'} - {trip.endTime || '--:--'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {trip.operatingDays?.map(d => d.slice(0, 3)).join(', ') || 'No days'}
                            </p>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <UsersIcon className="h-4 w-4 text-gray-400 mr-1" />
                              {trip.studentCount || trip.currentEnrollment || 0}/{trip.capacity || 0}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(trip.status)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => handleViewTrip(trip)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="View"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditTrip(trip)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Edit"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTrip(trip)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                              {canManageTrip(trip) && trip.status === 'active' && (
                                <Button
                                  onClick={() => handleStartTrip(trip)}
                                  size="sm"
                                  className="ml-2 bg-green-600 hover:bg-green-700"
                                >
                                  <PlayIcon className="h-3 w-3" />
                                </Button>
                              )}
                              {trip.status === 'in_progress' && (
                                <Button
                                  onClick={() => handleCompleteTrip(trip)}
                                  size="sm"
                                  className="ml-2 bg-red-600 hover:bg-red-700"
                                >
                                  <StopIcon className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalTrips)}</span> of{' '}
                    <span className="font-medium">{totalTrips}</span> trips
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      variant="secondary"
                      size="sm"
                    >
                      First
                    </Button>
                    <Button
                      onClick={() => setCurrentPage(p => p - 1)}
                      disabled={currentPage === 1}
                      variant="secondary"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      onClick={() => setCurrentPage(p => p + 1)}
                      disabled={currentPage === totalPages}
                      variant="secondary"
                      size="sm"
                    >
                      Next
                    </Button>
                    <Button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      variant="secondary"
                      size="sm"
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <TripSessionsManager
              institutionId={user?.institutionId}
              viewMode="all"
              driverId={user?.role === 'DRIVER' ? user.id : undefined}
            />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <TransportSettingsComponent institutionId={user?.institutionId} />
          )}
        </div>
      </div>

      {/* Create Trip Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold">Create New Trip</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <EnhancedTripForm
                institutionId={user?.institutionId}
                onSuccess={(trip) => {
                  setShowCreateModal(false)
                  fetchTrips()
                  fetchStats()
                  toast.success(`Trip "${trip.tripName}" created!`)
                }}
                onCancel={() => setShowCreateModal(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Trip Modal */}
      <AnimatePresence>
        {showViewModal && selectedTrip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold">Trip Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Trip Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${TRIP_TYPE_CONFIG[selectedTrip.tripType]?.bgColor || 'bg-gray-50'}`}>
                      {(() => {
                        const Icon = TRIP_TYPE_CONFIG[selectedTrip.tripType]?.icon || CalendarDaysIcon
                        return <Icon className={`w-7 h-7 ${TRIP_TYPE_CONFIG[selectedTrip.tripType]?.color || 'text-gray-600'}`} />
                      })()}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedTrip.tripName || `${selectedTrip.tripType} Trip`}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getTripTypeBadge(selectedTrip.tripType)}
                        {getStatusBadge(selectedTrip.status)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trip Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Route</p>
                    <p className="font-medium">{selectedTrip.route?.routeNumber}</p>
                    <p className="text-sm text-gray-600">{selectedTrip.route?.routeName}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Vehicle</p>
                    <p className="font-medium">{selectedTrip.vehicle?.vehicleNumber || 'Not assigned'}</p>
                    <p className="text-sm text-gray-600">
                      {selectedTrip.vehicle?.make} {selectedTrip.vehicle?.vehicleModel}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Schedule</p>
                    <p className="font-medium">{selectedTrip.startTime} - {selectedTrip.endTime}</p>
                    <p className="text-sm text-gray-600">
                      {selectedTrip.operatingDays?.map(d => d.slice(0, 3)).join(', ')}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Capacity</p>
                    <p className="font-medium">{selectedTrip.studentCount || selectedTrip.currentEnrollment || 0} / {selectedTrip.capacity}</p>
                    <p className="text-sm text-gray-600">Students enrolled</p>
                  </div>
                </div>

                {/* Driver Info */}
                {selectedTrip.primaryDriver && (
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Primary Driver</p>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <UserGroupIcon className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {selectedTrip.primaryDriver.firstName} {selectedTrip.primaryDriver.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{selectedTrip.primaryDriver.phone}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enrolled Students */}
                {(selectedTrip as any).enrolledStudents?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">
                      Enrolled Students ({(selectedTrip as any).enrolledStudents.length})
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedTrip as any).enrolledStudents.slice(0, 10).map((enrolled: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">
                                {enrolled.student?.firstName?.[0]}{enrolled.student?.lastName?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {enrolled.student?.firstName} {enrolled.student?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{enrolled.stop?.name || 'Unknown stop'}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${enrolled.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {enrolled.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ))}
                      {(selectedTrip as any).enrolledStudents.length > 10 && (
                        <p className="text-sm text-gray-500 text-center pt-2">
                          + {(selectedTrip as any).enrolledStudents.length - 10} more students
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedTrip.notes && (
                  <div className="border rounded-lg p-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</p>
                    <p className="text-sm text-gray-700">{selectedTrip.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowViewModal(false)
                      handleEditTrip(selectedTrip)
                    }}
                  >
                    <PencilSquareIcon className="h-4 w-4 mr-2" />
                    Edit Trip
                  </Button>
                  <Button onClick={() => setShowViewModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Trip Modal */}
      <AnimatePresence>
        {showEditModal && selectedTrip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
                <h2 className="text-lg font-semibold">Edit Trip</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const formData = new FormData(e.currentTarget)
                  handleUpdateTrip({
                    tripName: formData.get('tripName') as string,
                    status: formData.get('status') as any,
                    capacity: parseInt(formData.get('capacity') as string),
                    startTime: formData.get('startTime') as string,
                    endTime: formData.get('endTime') as string,
                    notes: formData.get('notes') as string,
                  })
                }}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trip Name</label>
                  <input
                    name="tripName"
                    type="text"
                    defaultValue={selectedTrip.tripName}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      name="startTime"
                      type="time"
                      defaultValue={selectedTrip.startTime}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      name="endTime"
                      type="time"
                      defaultValue={selectedTrip.endTime}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      defaultValue={selectedTrip.status}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                    <input
                      name="capacity"
                      type="number"
                      defaultValue={selectedTrip.capacity}
                      min={1}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    defaultValue={selectedTrip.notes || ''}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedTrip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">Delete Trip</h3>
                <p className="text-center text-gray-500 mb-6">
                  Are you sure you want to delete <span className="font-medium">"{selectedTrip.tripName}"</span>?
                  This action cannot be undone.
                </p>
                <div className="flex justify-center gap-3">
                  <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={confirmDeleteTrip}
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
